/**
 * View-state for Report Issue dialog → optional screenshot flow (T-002 / T-003).
 */

export type ReportIssuePhase = 'idle' | 'dialogOpen' | 'capturingScreenshot';

export interface ReportIssueViewState {
    phase: ReportIssuePhase;
    /** Object URL or data URL for PNG preview; null when capture failed / omitted */
    previewUrl: string | null;
}

export type ReportIssueViewEvent =
    | { type: 'OPEN' }
    | { type: 'START_SCREENSHOT' }
    | { type: 'SCREENSHOT_SUCCEEDED'; previewUrl: string }
    | { type: 'SCREENSHOT_FAILED' }
    | { type: 'RETAKE' }
    | { type: 'CLOSE' };

export const INITIAL_REPORT_ISSUE_VIEW_STATE: ReportIssueViewState = {
    phase: 'idle',
    previewUrl: null,
};

/** Client fallback when status fails without a parseable server error. */
export const REPORT_ISSUE_STATUS_ALERT_FALLBACK = 'Issue reporting is unavailable. Please try again later.';

/**
 * Pure reducer: dialog opens immediately on OPEN; screenshot is optional via
 * START_SCREENSHOT / RETAKE; capture overlay while capturingScreenshot.
 */
export function reduceReportIssueViewState(state: ReportIssueViewState, event: ReportIssueViewEvent): ReportIssueViewState {
    switch (event.type) {
        case 'OPEN':
            return { phase: 'dialogOpen', previewUrl: null };
        case 'START_SCREENSHOT':
            return { phase: 'capturingScreenshot', previewUrl: null };
        case 'SCREENSHOT_SUCCEEDED':
            return { phase: 'dialogOpen', previewUrl: event.previewUrl };
        case 'SCREENSHOT_FAILED':
            return { phase: 'dialogOpen', previewUrl: null };
        case 'RETAKE':
            return { phase: 'capturingScreenshot', previewUrl: null };
        case 'CLOSE':
            return { phase: 'idle', previewUrl: null };
        default:
            return state;
    }
}

export type CanSubmitReportIssueArgs = {
    message: string;
    previewUrl: string | null;
    /** When true, Submit stays disabled (status preflight in flight). */
    statusLoading?: boolean;
    /**
     * When false, Submit stays disabled. When omitted (legacy callers),
     * treated as ok so message-only rules still apply.
     */
    statusOk?: boolean;
    /** When true, Submit stays disabled (create-issue in flight). */
    submitting?: boolean;
};

/**
 * Submit is allowed with a non-empty message even when screenshot is missing.
 * T-003: also gated on status preflight (loading / !ok) and in-progress submit.
 */
export function canSubmitReportIssue(args: CanSubmitReportIssueArgs): boolean {
    if (args.statusLoading) {
        return false;
    }
    if (args.submitting) {
        return false;
    }
    if (args.statusOk === false) {
        return false;
    }
    return args.message.trim().length > 0;
}

export type ReportIssueStatusAlertArgs = {
    statusLoading: boolean;
    statusOk: boolean;
    statusError: string | null;
};

/**
 * Alert text for status preflight failures. Null while loading or when ok.
 */
export function getReportIssueStatusAlert(args: ReportIssueStatusAlertArgs): string | null {
    if (args.statusLoading || args.statusOk) {
        return null;
    }
    const trimmed = args.statusError?.trim();
    if (trimmed) {
        return trimmed;
    }
    return REPORT_ISSUE_STATUS_ALERT_FALLBACK;
}
